import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  equipmentDocumentsApi,
  type EquipmentDocument,
  type EquipmentDocumentLists,
} from '@/api/equipmentDocumentsApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Documents on an equipment type or a unit (ClickUp z8nrz7cnj9).
 *
 * `tenantId` is in every key because documents are tenant-scoped and an id from
 * one tenant is meaningless in another — without it a switch could paint the
 * previous tenant's list against the new tenant's owner id.
 *
 * A unit's list depends on its TYPE's documents too, so an upload against a
 * type has to reach every unit's cached list. Both owners therefore invalidate
 * the whole `['equipment-documents', tenantId]` prefix rather than their own
 * key: a type has no cheap way to know which units are currently rendered.
 */
const documentsKey = (tenantId: string | null, owner: 'type' | 'unit', ownerId: string) =>
  ['equipment-documents', tenantId, owner, ownerId] as const

function useInvalidateDocuments() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return () => {
    void qc.invalidateQueries({ queryKey: ['equipment-documents', tenantId] })
  }
}

/** An equipment type's documents, plus upload/rename/remove. */
export function useEquipmentTypeDocuments(equipmentTypeId: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateDocuments()

  const list = useQuery<EquipmentDocument[]>({
    queryKey: documentsKey(tenantId, 'type', equipmentTypeId ?? ''),
    enabled: !!tenantId && !!equipmentTypeId,
    queryFn: () => equipmentDocumentsApi.listForType(equipmentTypeId as string),
  })

  const upload = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) =>
      equipmentDocumentsApi.uploadForType(equipmentTypeId as string, name, file),
    onSuccess: invalidate,
  })

  return { list, upload, ...useDocumentMutations() }
}

/**
 * A unit's own documents plus the read-only ones inherited from its type.
 *
 * The two lists arrive already separated from the API rather than being derived
 * here by comparing owner ids — the split is what the UI renders, and deriving
 * it client-side would put the rule in two places.
 */
export function useEquipmentUnitDocuments(equipmentId: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateDocuments()

  const list = useQuery<EquipmentDocumentLists>({
    queryKey: documentsKey(tenantId, 'unit', equipmentId ?? ''),
    enabled: !!tenantId && !!equipmentId,
    queryFn: () => equipmentDocumentsApi.listForEquipment(equipmentId as string),
  })

  const upload = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) =>
      equipmentDocumentsApi.uploadForEquipment(equipmentId as string, name, file),
    onSuccess: invalidate,
  })

  return { list, upload, ...useDocumentMutations() }
}

/** Rename and remove address a document by its own id, so they are owner-agnostic. */
function useDocumentMutations() {
  const invalidate = useInvalidateDocuments()

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      equipmentDocumentsApi.rename(id, name),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => equipmentDocumentsApi.remove(id),
    onSuccess: invalidate,
  })

  return { rename, remove }
}

/**
 * Fetch a fresh download URL and open it. Not a query — the URL is short-lived,
 * so caching one would just hand back an expired link.
 */
export async function openEquipmentDocument(id: string): Promise<void> {
  const { url } = await equipmentDocumentsApi.getDownloadUrl(id)
  window.open(url, '_blank', 'noopener,noreferrer')
}
